import { useState, type KeyboardEvent } from 'react';

interface Props {
  onSend: (body: string) => Promise<unknown> | unknown;
  disabled?: boolean;
  placeholder?: string;
}

export function Composer({ onSend, disabled, placeholder = 'Type a message…' }: Props) {
  const [value, setValue] = useState('');
  const [sending, setSending] = useState(false);

  const submit = async () => {
    const trimmed = value.trim();
    if (!trimmed || sending) return;
    setSending(true);
    try {
      await onSend(trimmed);
      setValue('');
    } finally {
      setSending(false);
    }
  };

  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void submit();
    }
  };

  return (
    <div
      className="border-t border-gray-200 p-2 flex gap-2 items-end bg-white"
      data-test-id="chat-composer"
    >
      <textarea
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={onKey}
        placeholder={placeholder}
        rows={1}
        disabled={disabled || sending}
        maxLength={4000}
        className="flex-1 resize-none border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300 max-h-32"
      />
      <button
        type="button"
        onClick={submit}
        disabled={disabled || sending || !value.trim()}
        className="rounded-lg bg-orange-500 px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
        data-test-id="chat-send-button"
      >
        {sending ? '…' : 'Send'}
      </button>
    </div>
  );
}
